import { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Circle, CircleMarker, Popup, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { money } from "@/lib/api";

const MI = 1609.34;

const Controller = ({ loc, radius, onPick }) => {
  const map = useMap();
  useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) });
  useEffect(() => {
    const t = setTimeout(() => {
      map.invalidateSize();
      if (radius) map.fitBounds(L_bounds(loc, radius));
      else map.setView([loc.lat, loc.lng], 10);
    }, 250);
    return () => clearTimeout(t);
  }, [map, loc, radius]);
  return null;
};

const L_bounds = (loc, r) => {
  const dLat = r / 69, dLng = r / (69 * Math.cos((loc.lat * Math.PI) / 180));
  return [[loc.lat - dLat, loc.lng - dLng], [loc.lat + dLat, loc.lng + dLng]];
};

const haversine = (a, lat, lng) => {
  const r = Math.PI / 180, dLat = (lat - a.lat) * r, dLng = (lng - a.lng) * r;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(lat * r) * Math.sin(dLng / 2) ** 2;
  return 7917.6 * Math.asin(Math.sqrt(h));
};

const groupPins = (items, loc, radius) => {
  const g = {};
  (items || []).forEach((i) => {
    if (radius && haversine(loc, i.map_lat, i.map_lng) > radius) return;
    const k = `${i.map_lat},${i.map_lng}`;
    (g[k] = g[k] || { lat: i.map_lat, lng: i.map_lng, items: [] }).items.push(i);
  });
  return Object.values(g);
};

export const LocationMap = ({ loc, radius, items, onPick }) => (
  <div data-testid="location-map" className="h-64 rounded-xl overflow-hidden border border-slate-200 relative z-0">
    <MapContainer center={[loc.lat, loc.lng]} zoom={10} scrollWheelZoom className="h-full w-full">
      <TileLayer attribution='&copy; OpenStreetMap contributors · ZIPs: GeoNames' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <Controller loc={loc} radius={radius} onPick={onPick} />
      {radius ? <Circle center={[loc.lat, loc.lng]} radius={radius * MI} pathOptions={{ color: "#2563eb", weight: 2, fillOpacity: 0.08 }} /> : null}
      {groupPins(items, loc, radius).map((p) => (
        <CircleMarker key={`${p.lat},${p.lng}`} center={[p.lat, p.lng]} radius={8 + Math.min(p.items.length, 6)} pathOptions={{ color: "#0f172a", weight: 1, fillColor: "#f59e0b", fillOpacity: 0.9 }}>
          <Popup>
            <p className="text-xs text-slate-500 mb-1">{p.items.length} item{p.items.length > 1 ? "s" : ""} · approximate area</p>
            <ul className="space-y-1 max-h-40 overflow-y-auto">
              {p.items.slice(0, 10).map((i) => <li key={i.id}><Link data-testid={`map-pin-link-${i.id}`} to={`/listing/${i.id}`} className="text-blue-700 font-semibold">{money(i.price)} · {i.title}</Link></li>)}
            </ul>
          </Popup>
        </CircleMarker>
      ))}
      <CircleMarker center={[loc.lat, loc.lng]} radius={7} pathOptions={{ color: "#fff", weight: 2, fillColor: "#2563eb", fillOpacity: 1 }} />
    </MapContainer>
  </div>
);
